import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import datasetsConfig from '@/data/datasets.json'
import { getDatasetOptions, getAllDatasetModules, getDatasetAccess } from '../config/datasets'

/**
 * Composable for dataset and view selection logic.
 *
 * @param {Object} options
 * @param {import('@vue/composition-api').Ref<string>|null} options.initialDataset - preset dataset key
 * @param {import('@vue/composition-api').Ref<string>|null} options.initialView - preset view key
 * @returns {Object} reactive dataset/view state and options
 */
export function useChartDatasets({ initialDataset = null, initialView = null } = {}) {
  const modules = getAllDatasetModules()
  const datasetKeys = Object.keys(modules)

  const selectedDataset = ref(
    initialDataset && datasetKeys.includes(initialDataset) ? initialDataset : ''
  )

  const selectedView = ref(initialView || '')

  // ─── Dataset options (filtered by user permissions — Épica 6) ────
  // Access is resolved via getDatasetAccess: a dataset is listed when the
  // primary permission OR any declared permissionFallback is satisfied.
  const datasetOptions = computed(() => {
    return getDatasetOptions().filter(opt => getDatasetAccess(opt.value).allowed)
  })

  // ─── Active access restriction for the selected dataset ──────────
  // Non-null when the user reached the dataset through a permission fallback
  // (e.g. BauComplement → operational_security): carries the forced `where`
  // clause and the filter keys that must be locked in the UI.
  const datasetRestriction = computed(() => {
    if (!selectedDataset.value) return null
    return getDatasetAccess(selectedDataset.value).restriction
  })

  // ─── View options (from dataset module config, filtered by admin) ─────
  const viewOptions = computed(() => {
    const module = modules[selectedDataset.value]
    if (!module || !module.views) return []

    const user = store.getters['user/getUser']
    const isAdmin = user && user.is_master_admin

    return module.views.filter(v => !v.adminOnly || isAdmin)
  })

  // ─── Has valid selection ──────────────────────────────
  const hasSelection = computed(() =>
    selectedDataset.value !== '' && selectedView.value !== ''
  )

  // ─── Config for current dataset ───────────────────────
  const currentDatasetConfig = computed(() =>
    datasetsConfig[selectedDataset.value] || null
  )

  // ─── When dataset changes, validate view ──────────────
  watch(selectedDataset, () => {
    const available = viewOptions.value.map(v => v.value)

    if (available.length === 0) {
      selectedView.value = ''
      return
    }

    if (!available.includes(selectedView.value)) {
      selectedView.value = available[0] || ''
    }
  })

  return {
    selectedDataset,
    selectedView,
    datasetOptions,
    datasetRestriction,
    viewOptions,
    hasSelection,
    currentDatasetConfig,
  }
}
